import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Instagram, Send } from 'lucide-react';
import { contactInfo, BOOKING_URL } from '../data/services';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service_interest: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-24 bg-chalk min-h-screen">
      {/* Hero */}
      <section className="py-16 lg:py-24 text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="font-cinzel text-amaranth text-xs tracking-[0.3em] mb-4">
            GET IN TOUCH
          </p>
          <h1 className="font-serif text-dark text-4xl lg:text-6xl mb-6">
            Contact Us
          </h1>
          <p className="text-dark-secondary max-w-2xl mx-auto">
            We'd love to hear from you. Reach out to schedule a consultation 
            or ask any questions about our treatments.
          </p>
        </motion.div>
      </section>

      {/* Main Content */}
      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-dark text-3xl mb-8">
                Visit Our Sanctuary
              </h2>

              <div className="space-y-8">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-thulian/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-amaranth" size={20} />
                  </div>
                  <div>
                    <h3 className="font-serif text-dark text-lg mb-1">Location</h3>
                    <p className="text-dark-secondary">
                      {contactInfo.address}<br />
                      {contactInfo.suite}<br />
                      {contactInfo.city}
                    </p>
                    <p className="text-dark-secondary text-sm mt-2">
                      Located near Topanga Mall, Canoga Park
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-thulian/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="text-amaranth" size={20} />
                  </div>
                  <div>
                    <h3 className="font-serif text-dark text-lg mb-1">Phone</h3>
                    <a 
                      href={`tel:${contactInfo.phone}`}
                      className="text-dark-secondary hover:text-amaranth transition-colors"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-thulian/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-amaranth" size={20} />
                  </div>
                  <div>
                    <h3 className="font-serif text-dark text-lg mb-1">Email</h3>
                    <a 
                      href={`mailto:${contactInfo.email}`}
                      className="text-dark-secondary hover:text-amaranth transition-colors"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-thulian/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="text-amaranth" size={20} />
                  </div>
                  <div>
                    <h3 className="font-serif text-dark text-lg mb-1">Hours</h3>
                    <p className="text-dark-secondary">
                      {contactInfo.hours.weekdays}<br />
                      {contactInfo.hours.saturday}<br />
                      {contactInfo.hours.sunday}
                    </p>
                  </div>
                </div>

                {/* Instagram */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-thulian/10 flex items-center justify-center flex-shrink-0">
                    <Instagram className="text-amaranth" size={20} />
                  </div>
                  <div>
                    <h3 className="font-serif text-dark text-lg mb-1">Instagram</h3>
                    <a 
                      href="https://instagram.com/aphrodite_med_spa"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dark-secondary hover:text-amaranth transition-colors"
                    >
                      {contactInfo.instagram}
                    </a>
                  </div>
                </div>
              </div>

              {/* Book Button */}
              <div className="mt-10">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-book-btn"
                  className="btn-luxury inline-flex items-center gap-2 bg-amaranth text-white px-8 py-4 rounded-full font-medium"
                >
                  Book Appointment Online
                </a>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-light p-8 lg:p-12">
                <h2 className="font-serif text-dark text-3xl mb-6">
                  Send Us a Message
                </h2>
                <p className="text-dark-secondary mb-8">
                  Have questions? Fill out the form below and we'll get back to you 
                  within 24 hours.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        data-testid="contact-name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="bg-white border-dark/10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="bg-white border-dark/10"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="contact-phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="bg-white border-dark/10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service">Interested In</Label>
                      <Select
                        value={formData.service_interest}
                        onValueChange={(value) => setFormData({ ...formData, service_interest: value })}
                      >
                        <SelectTrigger 
                          id="service"
                          data-testid="contact-service"
                          className="bg-white border-dark/10"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="injectables">Aesthetic Injectables</SelectItem>
                          <SelectItem value="fillers">Dermal Fillers</SelectItem>
                          <SelectItem value="lips">Lip Enhancement</SelectItem>
                          <SelectItem value="morpheus8">Morpheus8</SelectItem>
                          <SelectItem value="iv-therapy">IV Therapy</SelectItem>
                          <SelectItem value="peptides">Peptide Therapy</SelectItem>
                          <SelectItem value="weight-loss">Weight Loss</SelectItem>
                          <SelectItem value="consultation">General Consultation</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      data-testid="contact-message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={5}
                      className="bg-white border-dark/10 resize-none"
                      placeholder="Tell us how we can help you..."
                    />
                  </div>

                  {submitStatus === 'success' && (
                    <div className="p-4 bg-brook/20 text-dark text-sm">
                      Thank you for your message! We'll be in touch soon.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-amaranth/20 text-dark text-sm">
                      Something went wrong. Please try again or call us directly.
                    </div>
                  )}

                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={isSubmitting}
                    className="w-full bg-amaranth hover:bg-amaranth/90 text-white py-6 rounded-full"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send size={18} className="ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-[400px] w-full">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3302.123456789!2d-118.601234!3d34.181234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDEwJzUyLjQiTiAxMTjCsDM2JzA0LjQiVw!5e0!3m2!1sen!2sus!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Aphrodite Med Spa Location"
          data-testid="contact-map"
        />
      </section>
    </main>
  );
};

export default ContactPage;
